import { useCpk } from '@swarm/core/contracts/cpk'
import { unifyAddressToId, useAccount } from '@swarm/core/web3'
import { ChildrenProps } from '@swarm/types'
import { ExtendedSecurity } from '@swarm/types/normalized-entities'
import { createContext, useContext, useMemo } from 'react'
import { useQueryParam } from 'use-query-params'

import useSecurities from 'src/hooks/staking/useSecurities'
import useSecuritiesRewards from 'src/hooks/staking/useSecuritiesRewards'

import { AssetParam } from './consts'

interface StakingContextType {
  securities: ExtendedSecurity[]
  securitiesRewards: Record<string, number>
  securitiesLoading: boolean
  rewardsLoading: boolean
}

export const StakingContext = createContext<StakingContextType>({
  securities: [],
  securitiesRewards: {},
  securitiesLoading: false,
  rewardsLoading: false,
})

export const StakingContextProvider = ({ children }: ChildrenProps) => {
  const cpk = useCpk()
  const account = useAccount()

  const [assetsParam] = useQueryParam('assets', AssetParam)

  const { securities, loading: securitiesLoading } = useSecurities({
    variables: {
      includeStakes: Boolean(cpk?.address && account),
      securityFilter: {
        ...(assetsParam.length > 0 && {
          // eslint-disable-next-line camelcase
          id_in: assetsParam,
        }),
      },
      ...(cpk?.address &&
        account && {
          stakesFilter: {
            unstaked: false,
            // eslint-disable-next-line camelcase
            maker_in: [
              unifyAddressToId(cpk.address),
              unifyAddressToId(account),
            ],
          },
        }),
    },
  })

  const { securitiesRewards, loading: rewardsLoading } = useSecuritiesRewards()

  const value = useMemo(
    () => ({
      securities,
      securitiesRewards,
      securitiesLoading,
      rewardsLoading,
    }),
    [rewardsLoading, securities, securitiesLoading, securitiesRewards],
  )

  return (
    <StakingContext.Provider value={value}>{children}</StakingContext.Provider>
  )
}

export const useStakingContext = () => useContext(StakingContext)
